import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config';
import { fetchData } from '../../utils/api';
import { useAdminLanguage } from '../../AdminLanguageContext';
import { translateAdmin } from '../../utils/adminTranslations';
import { boolElemByRole } from '../../utils/dynamic';
import userStyles from '../../styles/scss/system/Users.module.scss';
import styles from '../../styles/scss/system/OnlineSettings.module.scss';
import PaymentPeriods from '../../components/system/PaymentPeriods';

const OnlineSettings = ({ setHeaderTitle }) => {
  const { language } = useAdminLanguage();
  const translate = (text) => translateAdmin(text, language);
  const canManageOnline = boolElemByRole(['admin', 'booker-admin']);
  const canManagePeriods = boolElemByRole(['admin', 'booker-admin']);
  const [online, setOnline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await fetchData(
        `${apiUrl}/api/sys/online-status`,
        'GET',
        localStorage.getItem('accessToken'),
        { cache: false }
      );
      if (response.request.status !== 0) {
        throw new Error(response.request.message || 'Не удалось загрузить состояние системы');
      }
      setOnline(response.request.data.online);
    } catch (loadError) {
      console.error('Ошибка загрузки состояния системы:', loadError);
      setError(loadError.message || 'Не удалось загрузить состояние системы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setHeaderTitle(translate('Настройки сайта'));
    if (canManageOnline) loadStatus();
  }, [canManageOnline, language, loadStatus, setHeaderTitle]);

  const handleToggle = async () => {
    if (online === null || saving) return;

    const nextOnline = !online;
    const confirmation = nextOnline
      ? 'Включить онлайн-бронирование для клиентов?'
      : 'Выключить онлайн-бронирование? Клиенты не смогут искать и оплачивать брони.';
    if (!window.confirm(confirmation)) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetchData(
        `${apiUrl}/api/sys/online-status`,
        'PUT',
        localStorage.getItem('accessToken'),
        { body: { online: nextOnline } }
      );
      if (response.request.status !== 0) {
        throw new Error(response.request.message || 'Не удалось сохранить состояние системы');
      }
      setOnline(response.request.data.online);
      setMessage(nextOnline
        ? 'Онлайн-бронирование включено'
        : 'Онлайн-бронирование выключено');
    } catch (saveError) {
      console.error('Ошибка изменения состояния системы:', saveError);
      setError(saveError.message || 'Не удалось сохранить состояние системы');
    } finally {
      setSaving(false);
    }
  };

  const isOnline = online === true;

  return (
    <section className={`${userStyles.usersPage} ${styles.settingsPage}`}>
      <header className={userStyles.pageHeader}>
        <div>
          <span className={userStyles.eyebrow}>{translate('УПРАВЛЕНИЕ САЙТОМ')}</span>
          <h2>{translate('Настройки сайта')}</h2>
          <p>{translate('Управление онлайн-бронированием и периодами оплаты')}</p>
        </div>
        {canManageOnline && (
          <div className={styles.headerStatus} aria-live="polite">
            <span className={`${styles.statusDot} ${
              online === null
                ? styles.statusDotUnknown
                : isOnline
                  ? styles.statusDotOnline
                  : styles.statusDotOffline
            }`} />
            {loading
              ? translate('Загрузка состояния')
              : online === null
                ? translate('Состояние неизвестно')
                : isOnline
                  ? translate('Система включена')
                  : translate('Система выключена')}
          </div>
        )}
      </header>

      {canManageOnline && (
        <div className={userStyles.tableCard}>
          <div className={userStyles.tableHeading}>
            <div>
              <h3>{translate('Доступность онлайн-бронирования')}</h3>
              <span>{translate('Изменение применяется сразу после сохранения')}</span>
            </div>
            <button
              type="button"
              className={userStyles.secondaryButton}
              onClick={loadStatus}
              disabled={loading || saving}
            >
              {translate('Обновить')}
            </button>
          </div>

          <div className={styles.settingsContent} aria-busy={loading || saving}>
            {error && <div className={styles.errorMessage} role="alert">{translate(error)}</div>}
            {message && <div className={styles.successMessage} role="status">{translate(message)}</div>}

            <div className={`${styles.controlCard} ${
              online === null
                ? styles.controlCardUnknown
                : isOnline
                  ? styles.controlCardOnline
                  : styles.controlCardOffline
            }`}>
              <div className={styles.controlCopy}>
                <span className={styles.controlEyebrow}>{translate('ТЕКУЩИЙ РЕЖИМ')}</span>
                <h4>{loading
                  ? translate('Проверяем состояние...')
                  : online === null
                    ? translate('Состояние не загружено')
                    : isOnline
                      ? translate('Онлайн-система активна')
                      : translate('Ведутся технические работы')}</h4>
                <p>
                  {loading
                    ? translate('Получаем актуальное состояние с сервера.')
                    : online === null
                      ? translate('Обновите состояние и повторите попытку.')
                      : isOnline
                        ? translate('Клиенты могут искать свободные номера и оформлять бронирования.')
                        : translate('Поиск и оформление онлайн-бронирований для клиентов отключены.')}
                </p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={isOnline}
                aria-label={translate('Включить или выключить онлайн-бронирование')}
                className={`${styles.toggle} ${isOnline ? styles.toggleOn : ''}`}
                onClick={handleToggle}
                disabled={loading || saving || online === null}
              >
                <span className={styles.toggleThumb} />
              </button>
            </div>

            <div className={styles.notice}>
              <strong>{saving ? translate('Сохраняем настройку...') : translate('Что изменится?')}</strong>
              <p>
                {translate('При выключении клиенты увидят сообщение о технических работах, а запросы поиска номеров и оплаты будут отклоняться сервером. Админка останется доступной.')}
              </p>
            </div>
          </div>
        </div>
      )}

      {canManagePeriods && (
        <div className={userStyles.tableCard}>
          <div className={userStyles.tableHeading}>
            <div>
              <h3>{translate('Периоды оплаты')}</h3>
              <span>{translate('Настройка сезонных правил оплаты онлайн-бронирований')}</span>
            </div>
          </div>
          <PaymentPeriods embedded />
        </div>
      )}
    </section>
  );
};

export default OnlineSettings;
